<template>
  <div class="page-section tu-scope upd-page">
    <div :class="containerClass">

      <div v-if="isLoading" class="text-center py-5"><b-spinner /></div>

      <template v-else-if="program">
        <header class="upd-hero">
          <div v-if="coverImage" class="upd-cover" :style="{ backgroundImage: `url(${coverImage})` }" aria-hidden="true"></div>
          <span v-if="get(program, 'program.abbr')" class="tu-credential-badge upd-hero-chip">{{ get(program, 'program.abbr') }}</span>
          <h1 class="upd-hero-title">{{ get(program, 'program.title') }}</h1>
          <router-link v-if="schoolName" :to="{ name: 'std-cc-uni-checklist', params: { slug: schoolSlug } }" class="upd-hero-school upd-hero-school--under">
            <span class="upd-hero-offered">Offered by</span>
            <span class="upd-hero-logo" aria-hidden="true">
              <img v-if="schoolLogo" :src="schoolLogo" alt="" />
              <span v-else class="upd-hero-initial">{{ (schoolName || "?").charAt(0) }}</span>
            </span>
            <span class="upd-hero-schooltext">
              <b>{{ schoolName }}</b>
              <span v-if="heroLocation">{{ heroLocation }}</span>
            </span>
          </router-link>
        </header>

        <div class="tu-layout">
        <aside class="tu-rail" aria-label="Program summary">
          <div class="tu-rail-card">
            <h2 class="upd-rail-title">Plan your application</h2>
            <dl class="tu-facts upd-facts">
              <template v-if="keyDates.length">
                <div v-for="d in keyDates" :key="`kd-${d.label}`" class="upd-facts-date"><dt>{{ d.label }}</dt><dd>{{ d.value }}</dd></div>
              </template>
              <div v-else><dt>Key dates</dt><dd class="upd-facts-soft">Confirm with the school</dd></div>
            </dl>
            <button type="button" class="tu-btn tu-btn--primary upd-rail-cta" @click="explorePaths"><md-icon>directions</md-icon> Find a path</button>
            <a v-if="schoolSiteUrl" :href="schoolSiteUrl" target="_blank" rel="noopener noreferrer" class="tu-btn tu-btn--ghost upd-rail-apply">{{ get(program, 'enroll_link') ? 'Application website' : 'School website' }} <md-icon>open_in_new</md-icon></a>
            <p class="tu-rail-note">Explore routes to this program and build your application checklist.</p>
          </div>
        </aside>
        <div class="upd-main">
          <tu-section-tabs :items="sectionTabs" />
          <section id="upd-overview" class="pd-block">
            <h2 class="tu-section-title">Overview</h2>
            <div v-if="statTiles.length" class="upd-stats" aria-label="At a glance">
              <div v-for="t in statTiles" :key="t.label" class="upd-stat">
                <span>{{ t.label }}</span>
                <strong>{{ t.value }}</strong>
                <small v-if="t.sub">{{ t.sub }}</small>
              </div>
            </div>
            <div v-if="aboutHtml" class="pd-prose upd-prose" v-html="aboutHtml" />
            <p v-else class="upd-prose">Contact the school for full program details and entry requirements.</p>
          </section>


          <section v-if="relatedPathways.length" id="upd-route" class="pd-block">
            <h2 class="tu-section-title">Your route into {{ get(program, 'program.abbr') || get(program, 'program.title') }}</h2>
            <p class="tu-section-lead">Compare pathways to this program and plan your next step.</p>
            <div class="tu-card-grid upd-paths">
              <pathway-card v-for="p in relatedPathways" :key="p.id" :pathway="p" :enrolled="enrolledPathwayIds.has(p.id)" @view="viewPathway" />
            </div>
          </section>

          <section v-if="get(program, 'program.emp_description')" id="upd-career" class="pd-block">
            <h2 class="tu-section-title">Career outlook</h2>
            <p class="upd-prose">{{ get(program, 'program.emp_description') }}</p>
          </section>

          <section v-if="get(program, 'how_to_description') || get(program, 'how_to_video')" id="upd-apply" class="pd-block">
            <h2 class="tu-section-title">{{ get(program, 'how_to_title') || 'How to apply' }}</h2>
            <p v-if="get(program, 'how_to_description')" class="upd-prose">{{ get(program, 'how_to_description') }}</p>
            <div v-if="howToEmbed" class="upd-video-wrap">
              <iframe :src="howToEmbed" title="How to apply video" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
            </div>
            <video v-else-if="get(program, 'how_to_video')" class="upd-video" controls :src="get(program, 'how_to_video')"></video>
          </section>
        </div>
        </div>
      </template>

      <div v-else class="tu-empty">
        <md-icon>school</md-icon>
        <h4 class="tu-empty-title">Program not found</h4>
        <p class="tu-empty-text">This program isn't listed for that school anymore.</p>
        <router-link :to="{ name: 'student-programs' }" class="tu-btn tu-btn--ghost tu-empty-cta">Browse programs</router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { get } from 'lodash';
import { mapActions } from 'vuex';
import Page from '@/components/Page.vue';
import PathwayCard from '../Pathways/PathwayCard.vue';
import TuSectionTabs from '@/components/Ui/TuSectionTabs.vue';
import { programCardDetails } from '@/common/tuProgram';

/**
 * Student portal program page. Mirrors the public program page on
 * transitionu.health (header, Overview, Program details, route in, career
 * outlook, how to apply) but keeps the student inside the portal.
 */
export default {
  name: 'ProgramDetail',
  components: { PathwayCard, TuSectionTabs },
  extends: Page,

  data() {
    return {
      title: 'Program',
      isLoading: true,
      program: null,
      university: {},
      pathways: [],
      myEnrollments: [],
    };
  },

  computed: {
    sectionTabs() {
      const tabs = [{ id: 'upd-overview', label: 'Overview', icon: 'info' }];
      if (this.relatedPathways.length) tabs.push({ id: 'upd-route', label: 'Your route', icon: 'directions' });
      if (get(this.program, 'program.emp_description')) tabs.push({ id: 'upd-career', label: 'Career outlook', icon: 'work' });
      if (get(this.program, 'how_to_description') || get(this.program, 'how_to_video')) tabs.push({ id: 'upd-apply', label: 'How to apply', icon: 'how_to_reg' });
      return tabs;
    },
    schoolSlug() {
      return this.$route.params.uniSlug;
    },
    schoolSiteUrl() {
      return get(this.program, 'enroll_link') || get(this.program, 'university.website') || this.university.website || '';
    },
    schoolName() {
      return get(this.program, 'university.name') || this.university.name || '';
    },
    schoolLogo() {
      const u = get(this.program, 'university') || this.university || {};
      return u.small_logo_url || u.logo_url || '';
    },
    heroLocation() {
      const u = get(this.program, 'university') || this.university || {};
      return [u.city, u.state].filter(Boolean).join(', ');
    },
    coverImage() {
      return get(this.program, 'program.image_url') || get(this.program, 'university.campus_img_url') || this.university.campus_img_url || '';
    },
    aboutHtml() {
      return get(this.program, 'description') || get(this.program, 'program.description') || '';
    },
    statTiles() {
      if (!this.program) return [];
      const tiles = programCardDetails(this.program);
      const hasDuration = tiles.some(t => t.label === 'Duration');
      const from = get(this.program, 'program.week_length_from');
      const to = get(this.program, 'program.week_length_to');
      if (!hasDuration && (from || to)) {
        const unit = get(this.program, 'program.duration_type') || 'weeks';
        tiles.push({ value: from && to && from !== to ? `${from}–${to} ${unit}` : `${to || from} ${unit}`, label: 'Duration' });
      }
      return tiles;
    },
    keyDates() {
      const p = this.program;
      if (!p) return [];
      const verified = get(p, 'dates_verified_at');
      if (!verified) return [];
      const ageDays = (Date.now() - new Date(verified).getTime()) / 86400000;
      if (!(ageDays >= 0 && ageDays <= 365)) return [];
      const dates = [];
      if (get(p, 'admission_cycle') === 'rolling') dates.push({ label: 'Admissions', value: 'Rolling, apply anytime' });
      const fields = [
        ['application_opening_date', 'Applications open'],
        ['application_deadline', 'Application deadline'],
        ['document_deadline', 'Documents due'],
        ['entrance_exam_deadline', 'Entrance exam by'],
        ['decision_release_date', 'Decisions released'],
        ['next_start_date', 'Next start'],
      ];
      for (const [field, label] of fields) {
        const raw = get(p, field);
        if (!raw) continue;
        const dt = new Date(`${String(raw).slice(0, 10)}T00:00:00Z`);
        if (!Number.isFinite(dt.getTime())) continue;
        dates.push({ label, value: dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) });
      }
      const terms = get(p, 'start_terms');
      if (!get(p, 'next_start_date') && Array.isArray(terms) && terms.length) {
        dates.push({ label: 'Start terms', value: terms.map(t => t.charAt(0).toUpperCase() + t.slice(1)).join(', ') });
      }
      return dates;
    },
    relatedPathways() {
      const slug = get(this.program, 'program.slug');
      if (!slug) return [];
      return this.pathways.filter(p => get(p, 'to_program.slug') === slug && p.is_active !== false);
    },
    enrolledPathwayIds() {
      return new Set(this.myEnrollments.map(e => get(e, 'pathway.id') || get(e, 'pathway')).filter(Boolean));
    },
    howToEmbed() {
      const url = get(this.program, 'how_to_video') || '';
      const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/);
      return m ? `https://www.youtube.com/embed/${m[1]}` : null;
    },
  },

  methods: {
    ...mapActions('uni', ['getPublicUniOfferedProgram', 'getUniFromSlug']),
    ...mapActions('pathways', ['getAllPathways', 'getMyPathwayEnrollments']),
    get,
    explorePaths() {
      const el = this.$el.querySelector('#upd-route');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      else this.$router.push({ name: 'student-pathways' });
    },
    viewPathway(pathway) {
      this.$router.push({ name: 'student-pathway-detail', params: { id: pathway.id } });
    },
    async fetchAll() {
      this.isLoading = true;
      try {
        const [prog, uni, paths, enrollments] = await Promise.all([
          this.getPublicUniOfferedProgram({ uni_slug: this.$route.params.uniSlug, prog_slug: this.$route.params.programSlug }),
          this.getUniFromSlug(this.$route.params.uniSlug).catch(() => null),
          this.getAllPathways({ limit: 50, uni_offered_programs__university__slug: this.$route.params.uniSlug }).catch(() => null),
          this.getMyPathwayEnrollments({ limit: 100 }).catch(() => null),
        ]);
        const p = get(prog, 'data.data', get(prog, 'data'));
        this.program = p && get(p, 'program') ? p : null;
        this.university = get(uni, 'data.data', get(uni, 'data')) || {};
        const list = get(paths, 'data.results', get(paths, 'data', [])) || [];
        this.pathways = Array.isArray(list) ? list : [];
        const en = get(enrollments, 'data.results', get(enrollments, 'data', [])) || [];
        this.myEnrollments = Array.isArray(en) ? en : [];
      } catch (err) {
        this.program = null;
      }
      this.isLoading = false;
    },
  },

  watch: {
    '$route.params.programSlug'() {
      this.fetchAll();
    },
  },

  async mounted() {
    await this.fetchAll();
  },
};
</script>

<style lang="scss" scoped>
.upd-back { display: inline-flex; align-items: center; gap: 4px; margin: 0 0 14px; font-size: 13px; font-weight: 600; color: var(--tu-ink-soft) !important; text-decoration: none !important; .md-icon { font-size: 18px; } &:hover { color: var(--tu-ink) !important; } }
.upd-hero { display: flex; flex-direction: column; align-items: flex-start; padding: 8px 0 16px; }
.upd-cover { width: 100%; height: clamp(160px, 22vw, 260px); margin-bottom: -22px; border-radius: 18px; background-size: cover; background-position: center 35%; background-repeat: no-repeat; background-color: var(--tu-surface-2); }
.upd-cover + .upd-hero-school { position: relative; z-index: 1; margin-left: 8px; box-shadow: 0 4px 14px rgba(24, 24, 24, 0.1); }
.upd-rail-cta { width: 100%; margin-top: 14px; }
.upd-rail-apply { width: 100%; margin-top: 10px; }
.upd-facts-soft { font-weight: 500 !important; color: var(--tu-ink-mute) !important; }
.upd-stats { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin: 0 0 22px; @media (max-width: 760px) { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
.upd-stat { padding: 16px; border: 1px solid var(--tu-line); border-radius: 12px; background: var(--tu-surface); display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.upd-stat strong { font-size: 21px; font-weight: 700; line-height: 1.25; color: var(--tu-ink); overflow-wrap: anywhere; }
.upd-rail-title { margin: 0 0 10px; font-size: 17px; font-weight: 700; color: var(--tu-ink); }
.upd-stat span { font-size: 12.5px; font-weight: 600; color: var(--tu-ink-mute); }
.upd-stat small { font-size: 11.5px; color: var(--tu-ink-hint); }
.upd-hero-school { display: inline-flex; align-items: center; gap: 10px; margin-bottom: 14px; padding: 6px 14px 6px 6px; border-radius: 999px; background: var(--tu-surface); border: 1px solid var(--tu-line); text-decoration: none !important; color: var(--tu-ink) !important; }
.upd-hero-logo { display: inline-flex; align-items: center; justify-content: center; flex: none; width: 30px; height: 30px; border-radius: 50%; overflow: hidden; background: #fff; img { width: 100%; height: 100%; object-fit: contain; padding: 3px; } .md-icon { font-size: 16px; color: var(--tu-ink-soft); } }
.upd-hero-schooltext { display: flex; flex-direction: column; line-height: 1.25; min-width: 0; b { font-size: 13.5px; font-weight: 600; color: var(--tu-ink); } span { font-size: 12px; color: var(--tu-ink-mute); } }
.upd-hero-chip { margin-bottom: 10px; }
.upd-hero-title { margin: 0 0 18px; font-size: clamp(30px, 3.6vw, 40px); line-height: 1.1; color: var(--tu-ink); max-width: 18ch; }
.upd-main { min-width: 0; }
.upd-main .tu-sectiontabs { margin-top: 0; }
.upd-facts dt small { display: block; font-size: 11.5px; font-weight: 500; color: var(--tu-ink-hint); margin-top: 1px; }
.upd-facts-date dd { color: var(--tu-blue-deep); }
.pd-block + .pd-block { margin-top: 32px; padding-top: 26px; border-top: 1px solid var(--tu-line-soft); }
.upd-prose { max-width: 68ch; font-size: 15.5px; line-height: 1.65; color: var(--tu-ink-soft); margin: 0; }
.upd-paths { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.upd-video-wrap { position: relative; margin-top: 16px; padding-top: 56.25%; border-radius: 14px; overflow: hidden; background: #000; iframe { position: absolute; inset: 0; width: 100%; height: 100%; } }
.upd-video { width: 100%; margin-top: 16px; border-radius: 14px; }

.upd-cover { margin-bottom: 0; }
.upd-hero-chip { margin-top: 18px; }
.upd-hero-school--under { margin: 0 0 18px; padding: 0; border: 0; background: transparent; box-shadow: none !important; gap: 12px; }
.upd-cover + .upd-hero-school { margin-left: 0; }
.upd-hero-offered { font-size: 12px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--tu-ink-mute); margin-right: 2px; }
.upd-hero-school--under .upd-hero-logo { width: 40px; height: 40px; border-radius: 10px; border: 1px solid var(--tu-line); }
.upd-hero-initial { font-size: 16px; font-weight: 700; color: var(--tu-ink-mute); }
.upd-hero-school--under .upd-hero-schooltext b { font-size: 15px; }
.upd-hero-school--under .upd-hero-schooltext span { font-size: 13px; }
.upd-stats { margin: 16px 0 24px; }
</style>
